// Workflow runner: every step is AI-written code, run in the sandbox.
// Returns { ok, tables, results, error } for previews, the trail and
// outputs. Older saved workflows may contain built-in step types that are no
// longer supported; those steps fail with a clear message.
import { renderOutputs } from "../outputs";
import { stepInputs, tableLabel, WorkflowError } from "../runner";
import { bindSource } from "../sources";
import { SandboxError } from "./sandbox";

export const CODE_STEP = "code";
export const PREVIEW_TIMEOUT_MS = 15000;
export const FULL_RUN_TIMEOUT_MS = 5 * 60 * 1000;

const NATIVE_OFF = "Built-in steps are switched off in this version. Ask Ledgerate AI to rebuild this step.";

// What the user sees for a failed step; `errorInfo` keeps the parts the AI
// may see after sanitising (see features/workflows/ai/codeShape.js).
const errorText = (info) => {
  const where = info.line ? ` (line ${info.line})` : "";
  if (info.own || info.timeout) return `${info.message}${where}`;
  return `${info.name}: ${info.message}${where}`;
};

const TYPE_NAMES = { number: "numbers", date: "dates", text: "text", boolean: "true/false values" };

// What the user sees when one of the step's result checks fails.
export const checkWarning = (check) => {
  const quoted = (names) => (names || []).map((name) => `"${name}"`).join(", ");
  switch (check?.kind) {
    case "notEmpty":
      return "Check failed: the result has no rows.";
    case "rowsAtMostInput":
      return "Check failed: the result has more rows than the input.";
    case "rowsEqualInput":
      return "Check failed: the result doesn't have one row per input row.";
    case "hasColumns":
      return `Check failed: the result should have the columns ${quoted(check.columns)}.`;
    case "columnType":
      return `Check failed: "${check.column}" should only hold ${TYPE_NAMES[check.type] || check.type}.`;
    case "noBlanks":
      return `Check failed: "${check.column}" has blank cells.`;
    case "unique":
      return `Check failed: ${quoted(check.columns)} should be unique, but some rows repeat.`;
    default:
      return "Check failed.";
  }
};

// `runner(job, { timeoutMs })` runs the job in the sandbox (see
// features/workflows/data/sandboxClient.js) and resolves with { results, tables }.
export const runCodeSteps = async (workflow, sourceTables, { timeoutMs = PREVIEW_TIMEOUT_MS, runner } = {}) => {
  const tables = { ...sourceTables };
  const results = [];
  const known = new Set(Object.keys(sourceTables));
  const jobSteps = [];
  let blocked = null; // a step that can't be sent to the sandbox

  for (let index = 0; index < workflow.steps.length; index++) {
    const step = workflow.steps[index];
    const inputs = stepInputs(workflow, index);
    if (!inputs.length || inputs.some((id) => !known.has(id))) {
      blocked = { step, inputs, message: "Not connected. Link a source or an earlier step to this step." };
      break;
    }
    if (step.type !== CODE_STEP && !step.disabled) {
      blocked = { step, inputs, message: NATIVE_OFF };
      break;
    }
    jobSteps.push({ id: step.id, inputs, code: step.params?.code, values: step.params?.values || {}, checks: step.params?.checks || [], disabled: Boolean(step.disabled) });
    known.add(step.id);
  }

  const fail = (stepId, info) => {
    const result = results.find((r) => r.stepId === stepId);
    result.status = "error";
    result.errorInfo = info;
    result.error = errorText(info);
    return { ok: false, tables, results, error: new WorkflowError(`${tableLabel(workflow, stepId)}: ${result.error}`, { stepId }) };
  };

  if (jobSteps.length) {
    const used = new Set(jobSteps.flatMap((s) => s.inputs));
    const job = { steps: jobSteps, tables: Object.fromEntries(Object.entries(sourceTables).filter(([id]) => used.has(id))) };
    let output;
    try {
      output = await runner(job, { timeoutMs });
    } catch (error) {
      if (!(error instanceof SandboxError) || !error.timeout) throw error;
      // Timed out: report it on the step that was running.
      const stepId = error.stepId || jobSteps[0].id;
      jobSteps.slice(0, jobSteps.findIndex((s) => s.id === stepId) + 1).forEach((s) => results.push({ stepId: s.id, type: CODE_STEP, inputs: s.inputs, warnings: [] }));
      return fail(stepId, { name: "Timeout", message: error.message, line: null, own: true, timeout: true });
    }
    Object.assign(tables, output.tables);
    output.results.forEach(({ error, ...result }) => {
      const checks = workflow.steps.find((s) => s.id === result.stepId)?.params?.checks || [];
      const warnings = [...(result.warnings || []), ...(result.failedChecks || []).map((index) => checkWarning(checks[index]))];
      results.push({ ...result, warnings, type: CODE_STEP, ...(error ? { errorInfo: error } : {}) });
    });
    const failed = output.results.find((r) => r.status === "error");
    if (failed) return fail(failed.stepId, failed.error);
  }

  if (blocked) {
    results.push({ stepId: blocked.step.id, type: blocked.step.type, inputs: blocked.inputs, warnings: [] });
    return fail(blocked.step.id, { name: "Error", message: blocked.message, line: null, own: true });
  }
  return { ok: true, tables, results, error: null };
};

// Full run (Run on new files): bind each source's cells, run the code steps,
// then render the output files.
export const runCodeWorkflow = async (workflow, sourceCells, { columnOverrides = {}, timeoutMs = FULL_RUN_TIMEOUT_MS, runner } = {}) => {
  const issues = [];
  const sourceTables = {};
  workflow.sources.forEach((source) => {
    const cells = sourceCells[source.id];
    if (!cells) {
      issues.push({ level: "error", message: `No file provided for ${source.label || source.id}` });
      return;
    }
    const bound = bindSource(source, cells, { columnOverrides: columnOverrides[source.id] });
    issues.push(...bound.issues);
    sourceTables[source.id] = bound.table;
  });
  if (issues.some((issue) => issue.level === "error")) {
    return { ok: false, issues, results: [], tables: sourceTables, files: [], errors: [] };
  }

  const run = await runCodeSteps(workflow, sourceTables, { timeoutMs, runner });
  if (!run.ok) return { ok: false, issues, results: run.results, tables: run.tables, files: [], errors: [run.error.message] };
  const { files, errors } = renderOutputs(workflow, run.tables);
  return { ok: errors.length === 0, issues, results: run.results, tables: run.tables, files, errors };
};
